'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Footer from '../_components/_ui/Footer'

const supportPages = [
    { 
        name: 'FAQ',
        href: '/support/faq',
        description: "Got questions? We've got answers! Probably. Maybe. Let's find out! 🤔",
        icon: "❓",
        color: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20"
    },
    { 
        name: 'Contact Us',
        href: '/support/contact',
        description: "Need to reach a human? We're real people who love to help! Promise! 👋",
        icon: "📧",
        color: "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/20"
    },
    { 
        name: 'Server Status',
        href: '/support/status',
        description: "Check if everything's running smoothly. It usually is! *knocks on wood* 🧙‍♂️",
        icon: "🚦",
        color: "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20"
    },
    { 
        name: 'Custom Blocks and Styling',
        href: '/support/custom-css',
        description: "For the DIY designers who want to add their special touch. Make it yours! ✨",
        icon: "🎨",
        color: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20"
    },
    { 
        name: 'Developer Documentation',
        href: '/support/documentation',
        description: "Deep dive into Autofocus architecture, APIs, blocks, and technical capabilities. 🚀",
        icon: "📚",
        color: "bg-green-500/10 hover:bg-green-500/20 border-green-500/20"
    },
]

export default function SupportNav() {
    const pathname = usePathname()

    return (
        <div className="min-h-screen w-full relative flex flex-col">
            <BackgroundBeams />
            <div className="relative z-10 flex-grow">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white leading-tight"
                        >
                            Help & Support
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-sm text-gray-400 mt-2"
                        >
                            We're here to help you succeed. No question too small, no problem too big!
                        </motion.p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {supportPages.map((page, index) => (
                            <motion.div
                                key={page.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link href={page.href}>
                                    <div className={`p-6 rounded-xl border backdrop-blur-sm transition-all duration-300 ${page.color}`}>
                                        <div className="flex items-center gap-4">
                                            <span className="text-3xl">{page.icon}</span>
                                            <div>
                                                <h2 className="text-lg font-semibold text-white mb-2">
                                                    {page.name}
                                                </h2>
                                                <p className="text-sm text-gray-300">
                                                    {page.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {/* Quick Help Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="mt-12 p-6 rounded-xl border border-indigo-500/20 bg-indigo-500/10 backdrop-blur-sm"
                    >
                        <h3 className="text-lg font-semibold text-white mb-2">
                            Need Help Fast? ⚡
                        </h3>
                        <p className="text-sm text-gray-300">
                            Email us directly at <a href="mailto:help@autofoc.us" className="text-indigo-400 hover:text-indigo-300">help@autofoc.us</a> or 
                            join our <a href="https://discord.gg/autofocus" className="text-indigo-400 hover:text-indigo-300">Discord community</a> for 
                            real-time support from our team and other Autofoc.us users. We typically respond within 24 hours!
                        </p>
                    </motion.div>
                </div>
            </div>
            <Footer />
        </div>
    )
}
